import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EmptyState from './EmptyState';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import { RecordIdLabel } from './ui/idColumn';
import { useConfirm } from './ui/ConfirmProvider';
import { FormCheckbox, FormInput, FormTextarea } from './FormField';
import SearchableSelect from './ui/SearchableSelect';
import { useNotifications } from './NotificationProvider';
import BookingLines from './vendorBookings/BookingLines';
import { useLoad } from '../hooks/useLoad';
import {
  BOOKING_ARCHIVE_EVENT, BOOKING_RESTORE_EVENT, BOOKING_SAVE_EVENT, NO_BOOKING_EDITOR, setVendorBookingsBar,
} from '../hooks/useVendorBookingsBar';
import { useMoney } from '../hooks/useMoney';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { useRole } from '../hooks/useRole';
import { useStoredEvents } from '../hooks/useStoredEvents';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import {
  addBookingLine, archiveBooking, deleteBookingLine, fetchBooking, restoreBooking, updateBooking, updateBookingLine,
} from '../services/VendorBookingRecordService';
import { isApiError } from '../utils/apiErrors';
import { draftFromLine, draftTotal, lineErrors, planLineSave, type DraftLine } from '../utils/bookingLines';
import { formatDecimal } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDateTime } from '../utils/formatDate';
import { recordLabel } from '../utils/recordId';
import { buildBookingPatch, formFromBooking, validateBookingForm, type BookingForm } from '../utils/vendorBookingForm';
import {
  BOOKING_STATUS_LABELS, BOOKING_STATUS_VARIANT, bookingErrorMessage, nextStatuses, statusOf,
} from '../utils/vendorBookingLabels';
import type { BookingDetail, BookingPatch, BookingStatus } from '../types/VendorBookingRecord';
import '../styles/components/VendorBookings.css';

const yesNo = (v: boolean | null): string => (v === null ? '—' : v ? 'Yes' : 'No');

/** One booking: its status and signed-contract flag (owned here), the editable details, its lines, the quote it came from and its budget line. */
const VendorBookingEdit: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const money = useMoney();
  const events = useStoredEvents();
  const { showSuccess, showError } = useNotifications();
  const { canEdit, canViewFinancials } = useRole();

  const load = useLoad(() => fetchBooking(bookingId), [bookingId]);
  usePageRefresh(() => load.reload(), load.loading);
  const booking: BookingDetail | null = load.data;

  const [form, setForm] = useState<BookingForm | null>(null);
  const [drafts, setDrafts] = useState<DraftLine[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lineProblems, setLineProblems] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const keyCounter = useRef(0);
  const nextKey = useCallback(() => `new-${++keyCounter.current}`, []);

  useEffect(() => {
    if (!booking) return;
    setForm(formFromBooking(booking));
    setDrafts(booking.lines.map(draftFromLine));
    setErrors({});
    setLineProblems({});
  }, [booking]);

  const readOnly = !canEdit || !!booking?.archived;
  const currency = booking?.currency || money.currency || null;
  const noLines = drafts.length === 0;

  const patch = useMemo<BookingPatch>(
    () => (booking && form ? buildBookingPatch(form, booking, { canViewFinancials, noLines: booking.lines.length === 0 && noLines }) : {}),
    [booking, form, canViewFinancials, noLines],
  );
  const plan = useMemo(
    () => (booking && canViewFinancials ? planLineSave(booking.lines, drafts) : { create: [], update: [], remove: [] }),
    [booking, drafts, canViewFinancials],
  );
  const dirty = !readOnly && (Object.keys(patch).length > 0 || plan.create.length + plan.update.length + plan.remove.length > 0);
  useUnsavedChangesGuard(dirty);

  const set = (key: keyof BookingForm) => (e: { target: { value: string } }) =>
    setForm(f => (f ? { ...f, [key]: e.target.value } : f));

  /** Status and the signed flag are the booking's own and apply at once, each as its own change. */
  const apply = useCallback(async (change: BookingPatch, done: string) => {
    if (applying) return;
    setApplying(true);
    try {
      await updateBooking(bookingId, change);
      showSuccess(done);
      await load.reload();
    } catch (err) {
      showError(bookingErrorMessage(err, 'The change could not be saved. Please try again.'));
    } finally {
      setApplying(false);
    }
  }, [applying, bookingId, load, showError, showSuccess]);

  const changeStatus = async (value: string) => {
    if (!booking || value === statusOf(booking.status)) return;
    const status = value as BookingStatus;
    if (status === 'cancelled') {
      const ok = await confirm({
        title: 'Cancel this booking?', subject: recordLabel('vendorBooking', booking.id),
        description: 'The booking stays on file as cancelled, and the quote can be booked again.', confirmLabel: 'Cancel booking', cancelLabel: 'Keep booking', variant: 'danger',
      });
      if (!ok) return;
    }
    await apply({ status }, `Booking moved to ${BOOKING_STATUS_LABELS[status]}`);
  };

  const save = useCallback(async () => {
    if (!booking || !form || readOnly || saving) return;
    const found = validateBookingForm(form, { canViewFinancials, noLines });
    const lineFound = canViewFinancials ? lineErrors(drafts, true) : {};
    setErrors(found);
    setLineProblems(lineFound);
    if (Object.keys(found).length > 0 || Object.keys(lineFound).length > 0) return;
    setSaving(true);
    try {
      // Lines first: with lines the booking's total is their sum.
      for (const l of plan.remove) await deleteBookingLine(booking.id, l.id);
      for (const d of plan.update) {
        await updateBookingLine(booking.id, d.id as string, { description: d.description.trim(), quantity: d.quantity.trim(), unit_price: d.unit_price.trim() });
      }
      for (const d of plan.create) {
        await addBookingLine(booking.id, { description: d.description.trim(), quantity: d.quantity.trim(), unit_price: d.unit_price.trim() });
      }
      if (Object.keys(patch).length > 0) await updateBooking(booking.id, patch);
      showSuccess('Booking saved');
      await load.reload();
    } catch (err) {
      showError(bookingErrorMessage(err, 'The booking could not be saved. Please try again.'));
      await load.reload();
    } finally {
      setSaving(false);
    }
  }, [booking, form, readOnly, saving, canViewFinancials, noLines, drafts, plan, patch, load, showError, showSuccess]);

  const archive = async () => {
    if (!booking) return;
    const ok = await confirm({
      title: 'Archive this booking?', subject: recordLabel('vendorBooking', booking.id),
      description: 'It leaves the active list. You can restore it from the Archived view.', confirmLabel: 'Archive', variant: 'danger',
    });
    if (!ok) return;
    try {
      await archiveBooking(booking.id);
      showSuccess('Booking archived');
      navigate('/vendor-bookings');
    } catch (err) {
      showError(bookingErrorMessage(err, 'The booking could not be archived.'));
    }
  };

  const restore = async () => {
    if (!booking) return;
    try {
      await restoreBooking(booking.id);
      showSuccess('Booking restored');
      await load.reload();
    } catch (err) {
      showError(bookingErrorMessage(err, 'The booking could not be restored.'));
    }
  };

  // Save / Archive / Restore live in the Header's bar (VendorBookingEditBar): report what they need, take their clicks back.
  const archivedBooking = !!booking?.archived;
  useEffect(() => {
    setVendorBookingsBar({ editor: { canEdit: canEdit && !!booking, canSave: !readOnly, saving, dirty, archived: archivedBooking } });
  }, [canEdit, booking, readOnly, saving, dirty, archivedBooking]);
  useEffect(() => () => setVendorBookingsBar({ editor: NO_BOOKING_EDITOR }), []);
  useEffect(() => {
    const onSave = () => { void save(); };
    const onArchive = () => { void archive(); };
    const onRestore = () => { void restore(); };
    window.addEventListener(BOOKING_SAVE_EVENT, onSave);
    window.addEventListener(BOOKING_ARCHIVE_EVENT, onArchive);
    window.addEventListener(BOOKING_RESTORE_EVENT, onRestore);
    return () => {
      window.removeEventListener(BOOKING_SAVE_EVENT, onSave);
      window.removeEventListener(BOOKING_ARCHIVE_EVENT, onArchive);
      window.removeEventListener(BOOKING_RESTORE_EVENT, onRestore);
    };
  });
  if (load.error && isApiError(load.error) && load.error.status === 403) {
    return (<><div className="wt-container fade-in"><EmptyState icon="lock" title="You do not have access to this booking" /></div></>);
  }
  if (load.error) {
    return (<><div className="wt-container fade-in"><LoadErrorBox what="this booking" error={load.error} onRetry={load.reload} retrying={load.loading} /></div></>);
  }
  if (!booking || !form) {
    return (<><div className="wt-container fade-in"><p className="vb-note" role="status">Loading booking...</p></div></>);
  }

  const status = statusOf(booking.status);
  const statusOptions = [status, ...nextStatuses(status)].map(s => ({ value: s, label: BOOKING_STATUS_LABELS[s] }));
  const eventOptions = [
    { value: '', label: 'No event' },
    ...events.map(e => ({ value: e.id, label: e.title })),
    ...(booking.event_id && !events.some(e => e.id === booking.event_id) ? [{ value: booking.event_id, label: booking.event_name || 'Event' }] : []),
  ];
  const total = canViewFinancials ? (drafts.length > 0 ? draftTotal(drafts) : booking.total_amount) : null;
  const item = booking.budget_item;

  return (
    <>
      <div className="wt-container fade-in vb-detail">
        <div className="vb-title-row">
          <h2 className="heading-page">{booking.vendor_name || 'Vendor'} <span className="vb-note"><RecordIdLabel kind="vendorBooking" id={booking.id} /></span></h2>
          <StatusBadge variant={BOOKING_STATUS_VARIANT[status]}>{BOOKING_STATUS_LABELS[status]}</StatusBadge>
          {booking.archived && <StatusBadge variant="neutral">Archived</StatusBadge>}
        </div>

        <section className="vb-panel" aria-label="Status and contract">
          <h3 className="heading-card">Status and contract</h3>
          <div className="vb-form-grid">
            <div className="form-group">
              <span className="form-label" id="vb-status-label">Status</span>
              <SearchableSelect
                className="form-select" value={status} options={statusOptions} ariaLabel="Status" onChange={v => void changeStatus(v)}
                disabled={readOnly || applying}
              />
              {!readOnly && <span className="form-helper">Moving to Booked needs a deposit payment, a signed contract, or a booking date.</span>}
            </div>
            <div>
              <FormCheckbox
                checked={booking.contract_signed} disabled={readOnly || applying || status === 'contract-signed'}
                onChange={e => void apply({ contract_signed: e.target.checked }, e.target.checked ? 'Contract marked as signed' : 'Contract marked as not signed')}
                help={status === 'contract-signed' ? 'The status is Contract signed. Move it back to Booked to change this.' : undefined}
              >
                Contract signed
              </FormCheckbox>
            </div>
          </div>
          <p className="vb-note">The contract flag and the status are set here. The booking&apos;s budget line follows them.</p>
        </section>

        <section className="vb-panel" aria-label="Linked records">
          <h3 className="heading-card">Linked records</h3>
          <dl className="vb-kv">
            <dt>Quote</dt>
            <dd>
              {booking.quote_id ? (
                <Link className="vb-link" to={entityPath('quote', booking.quote_id) ?? '/quotes'}>
                  <RecordIdLabel kind="quote" id={booking.quote_id} />
                </Link>
              ) : 'Not made from a quote (an older booking)'}
              {booking.quote_id && booking.quote_status ? ` · ${booking.quote_status}` : ''}
            </dd>
            <dt>Budget line</dt>
            <dd>
              {item ? (
                canViewFinancials
                  ? <Link className="vb-link" to={entityPath('budgetCategory', item.category_id) ?? '/budget'}>{item.service || item.vendor_name || 'Open the budget line'}</Link>
                  : (item.service || item.vendor_name || 'Linked')
              ) : 'No budget line is linked yet'}
              {item ? <span className="vb-note"> · its contract flag, status and amount come from this booking</span> : null}
            </dd>
            <dt>Vendor</dt>
            <dd>{booking.vendor_name || '—'}</dd>
          </dl>
        </section>

        <section className="vb-panel" aria-label="Booking details">
          <h3 className="heading-card">Details</h3>
          <div className="vb-form-grid">
            <div className="form-group">
              <span className="form-label">Event</span>
              <SearchableSelect
                className="form-select" value={form.event_id} options={eventOptions} ariaLabel="Event" disabled={readOnly}
                onChange={v => setForm(f => (f ? { ...f, event_id: v } : f))}
              />
            </div>
            <FormInput label="Booking date" type="date" value={form.booking_date} onChange={set('booking_date')} disabled={readOnly} />
            <FormInput label="Confirmation reference" value={form.confirmation_ref} onChange={set('confirmation_ref')} disabled={readOnly} maxLength={255} />
            <FormInput label="Contract date" type="date" value={form.contract_date} onChange={set('contract_date')} disabled={readOnly} />
            <FormInput label="Contract link" type="url" inputMode="url" value={form.contract_link} onChange={set('contract_link')} error={errors.contract_link} disabled={readOnly} />
            <FormInput label="Contact method" value={form.contact_method} onChange={set('contact_method')} disabled={readOnly} maxLength={100} />
            <FormInput label="Last contact" type="date" value={form.last_contact_date} onChange={set('last_contact_date')} disabled={readOnly} />
            <FormInput label="Next follow-up" type="date" value={form.next_followup_date} onChange={set('next_followup_date')} disabled={readOnly} />
            <FormTextarea label="Special terms" rows={2} value={form.special_terms} onChange={set('special_terms')} disabled={readOnly} className="vb-span-all" />
            <FormTextarea label="Contact notes" rows={3} value={form.contact_notes} onChange={set('contact_notes')} disabled={readOnly} className="vb-span-all" />
          </div>
        </section>

        {canViewFinancials && (
          <section className="vb-panel" aria-label="Amounts and payments">
            <h3 className="heading-card">Amounts{currency ? ` (${currency})` : ''}</h3>
            <div className="vb-form-grid">
              <FormInput
                label="Total" inputMode="decimal" value={drafts.length > 0 ? total ?? '' : form.total_amount} onChange={set('total_amount')} error={errors.total_amount}
                disabled={readOnly || booking.lines.length > 0 || drafts.length > 0}
                help={drafts.length > 0 || booking.lines.length > 0 ? 'The sum of the lines.' : 'Add lines to price this booking, or enter a total.'}
              />
              <FormInput label="Contingency" inputMode="decimal" value={form.contingency_amount} onChange={set('contingency_amount')} error={errors.contingency_amount} disabled={readOnly} />
              <FormInput label="Deposit (%)" inputMode="numeric" value={form.deposit_percent} onChange={set('deposit_percent')} error={errors.deposit_percent} disabled={readOnly} />
              <FormInput label="Deposit due" type="date" value={form.deposit_due_date} onChange={set('deposit_due_date')} disabled={readOnly} />
              <FormInput label="Balance due" type="date" value={form.balance_due_date} onChange={set('balance_due_date')} disabled={readOnly} />
            </div>
            <dl className="vb-kv">
              <dt>Paid so far</dt><dd>{formatDecimal(booking.total_paid, currency)}</dd>
              <dt>Deposit paid</dt><dd>{yesNo(booking.deposit_paid)}</dd>
              <dt>Balance paid</dt><dd>{yesNo(booking.balance_paid)}</dd>
            </dl>
            <p className="vb-note">Payments are recorded on the Payments screen; these figures follow them.</p>
          </section>
        )}

        <section className="vb-panel" aria-label="Lines">
          <h3 className="heading-card">Lines</h3>
          <BookingLines
            lines={drafts} onChange={setDrafts} errors={lineProblems} disabled={readOnly} canViewFinancials={canViewFinancials}
            currency={currency} nextKey={nextKey}
          />
          {canViewFinancials && drafts.length > 0 && <p className="vb-total"><span>Total</span><span>{formatDecimal(total, currency)}</span></p>}
        </section>

        <p className="vb-note">Last changed {formatDateTime(booking.updated_at) || booking.updated_at}.</p>
      </div>
    </>
  );
};

export default VendorBookingEdit;
